import "styles/modal.scss";
import "./RREDocumentsDrawerTable.scss";
import "react-tooltip/dist/react-tooltip.css";

import cx from "classnames";
import EditableFilename from "components/Documents/shared/EditableFilename";
import type {
  DeleteModalState,
  DocumentType,
} from "components/Documents/DocumentTypes";
import { useDocumentTable } from "components/Documents/hooks/useDocumentTable";
import { useDocumentDropzone } from "components/Documents/hooks/useDocumentDropzone";
import DeleteConfirmationModal from "components/Documents/shared/DeleteConfirmationModal";
import PreviewButton from "components/Documents/preview/PreviewButton";
import LoadingIcon from "components/Loading/LoadingIcon";
import SearchBar from "components/SearchBar/SearchBar";
import Table from "components/Table/Table";
import { commonColumnProps } from "constants/TableConstants";
import { format, parseISO } from "date-fns";
import { useDownloadWithProgress } from "hooks/useDownloadWithProgress";
import { useCallback, useMemo, useState } from "react";
import type { CellClickArgs } from "react-data-grid";
import { Tooltip } from "react-tooltip";
import { setShowDocumentsDrawer } from "reducers/drawer";
import { useUpdateDocumentMutation } from "services/documents-api";
import { useGetOrganizationSettingsQuery } from "services/misc-api";
import type { RootState } from "services/store";
import { useToken } from "utils/context";
import { DATE_AND_TIME_FORMAT } from "utils/date-utils";
import { getDownloadFilenameForDocument } from "utils/document-utils";
import { useConstants } from "utils/hooks";
import { useDispatch, useSelector } from "utils/redux";

const formatDate = (dateString: string) => {
  return format(parseISO(dateString), DATE_AND_TIME_FORMAT);
};

type DocumentTableProps = {
  documents: DocumentType[];
  onDelete: (doc: DocumentType) => void;
};

// type for the rows in the table
type DocumentTableDataType = {
  id: number;
  original_filename: string;
  type: string;
  created: string;
  creator_name: string;
  preview_proxy_url: string | null;
  document: DocumentType; // store the original document for actions
};

const RREDocumentTable = ({ documents, onDelete }: DocumentTableProps) => {
  const { constants } = useConstants();
  const { accessToken } = useToken();
  const { startDownload } = useDownloadWithProgress();
  const [updateDocument] = useUpdateDocumentMutation();
  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(
    new Set(),
  );
  const [updatingDocIds, setUpdatingDocIds] = useState<number[]>([]);

  // transform documents to table rows
  const rows: DocumentTableDataType[] = documents.map((doc) => ({
    id: doc.id,
    original_filename: doc.original_filename,
    type: doc.type,
    created: doc.created,
    preview_proxy_url: doc.preview_proxy_url,
    creator_name: doc.creator_name,
    document: doc,
  }));

  const getDocumentTypeLabel = (type: string) => {
    return (
      constants?.DOCUMENT_TYPES?.find(
        (t: { value: string; label: string }) => t.value === type,
      )?.label || type
    );
  };

  const handleCellClick = (args: CellClickArgs<DocumentTableDataType>) => {
    if (args.column.key === "actions") return;
  };

  const handleSelectedRowsChange = (newSelectedRows: Set<string | number>) => {
    setSelectedRows(newSelectedRows);
  };

  const handleChangeDocumentType = useCallback(
    async (document: DocumentType, newType: string) => {
      if (!accessToken || updatingDocIds.includes(document.id)) return;

      try {
        setUpdatingDocIds((prev) => [...prev, document.id]);

        await updateDocument({
          documentId: document.id,
          data: {
            type: newType,
          },
        });
      } catch (error) {
        // console.error("Error toggling CMS status:", error);
      } finally {
        // setUpdatingDocIds((prev) => prev.filter((id) => id !== document.id));
      }
    },
    [accessToken, updatingDocIds, updateDocument],
  );

  const FilenameFormatter = ({ row }: { row: DocumentTableDataType }) => (
    <EditableFilename document={row.document} />
  );

  const columns = useMemo(() => {
    const documentColumns = [
      {
        key: "original_filename",
        name: "Document Name",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <FilenameFormatter row={row} />
        ),
      },
      {
        key: "created",
        name: "Uploaded",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <div>{formatDate(row.created)}</div>
        ),
      },
      {
        key: "creator_name",
        name: "Uploaded By",
        ...commonColumnProps,
      },
      {
        key: "type",
        name: "File Type",
        ...commonColumnProps,
        renderCell: ({ row }: { row: DocumentTableDataType }) => (
          <>
            <select
              className="document-type-select"
              onChange={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleChangeDocumentType(row.document, e.target.value);
              }}
            >
              {constants?.DOCUMENT_TYPES?.map((type) => (
                <option
                  key={type.value}
                  value={type.value}
                  selected={row.type === type.value}
                >
                  {getDocumentTypeLabel(type.value)}
                </option>
              ))}
            </select>
          </>
        ),
      },
      {
        key: "actions",
        name: "Actions",
        ...commonColumnProps,
        width: "135px",
        renderCell: (props: { row: DocumentTableDataType }) => {
          return (
            <div className="flex jc-se no-pd action-buttons">
              <PreviewButton
                document={props.row.document}
                tooltipId="preview-tooltip-claim"
                useNavigateForPreview={false}
              />
              <div
                className="material-icons edit"
                title="Download"
                onClick={(e) => {
                  e.preventDefault();

                  if (!accessToken) return;

                  startDownload({
                    url: props.row.document.proxy_url,
                    filename: getDownloadFilenameForDocument(
                      props.row.document.original_filename,
                      props.row.document.extension,
                    ),
                    totalBytes:
                      props.row.document.file_size_in_bytes || undefined,
                    onError: (error) => {
                      console.error("Download error:", error);
                    },
                  });
                }}
              >
                archive
              </div>
              <div
                className="material-icons delete"
                title="Delete"
                onClick={(e) => {
                  e.preventDefault();
                  onDelete(props.row.document);
                }}
              >
                delete
              </div>
            </div>
          );
        },
      },
    ];
    return documentColumns;
  }, [constants, onDelete, accessToken, startDownload]);

  return (
    <>
      <Table
        className="documents-table"
        columns={columns}
        rows={rows}
        rowKeyGetter={(row: DocumentTableDataType) => row.id}
        onCellClick={handleCellClick}
        onSelectedRowsChange={handleSelectedRowsChange}
        selectedRows={selectedRows}
      />
      {/* table positioning interferes with tooltips, so we add it here with an id */}
      <Tooltip
        id="preview-tooltip-claim"
        className="custom-tooltip"
        place="top"
      />
    </>
  );
};

type DocumentsProps = {
  rreId: number;
};

const RREDocuments = ({ rreId }: DocumentsProps) => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File[] | null>(null);
  const showDocumentsDrawer = useSelector(
    (state: RootState) => state.drawer.showDocumentsDrawer,
  );

  const {
    documents,
    isLoading,
    isUploading,
    handleUpload,
    handleDelete,
    documentSearchTerm,
  } = useDocumentTable({ rreId });

  const [deleteModalState, setDeleteModalState] = useState<DeleteModalState>({
    isOpen: false,
    document: null,
  });

  const handleDocumentUpload = async (files: File[], documentType?: string) => {
    await handleUpload(files, documentType);
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      // let useRREDocuments handle all validation and upload logic
      if (acceptedFiles && acceptedFiles.length > 0) {
        setSelectedFile(acceptedFiles);
        await handleDocumentUpload(acceptedFiles);
        setSelectedFile(null);
      }
    },
    [handleDocumentUpload],
  );

  const { data: organizationSettings } = useGetOrganizationSettingsQuery();

  const { getRootProps, getInputProps, isDragActive } = useDocumentDropzone({
    onDrop,
    allowMp4: !!organizationSettings?.allow_mp4_uploads,
  });

  const handleDocumentDelete = (doc: DocumentType) => {
    setDeleteModalState({ isOpen: true, document: doc });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModalState.document) return;

    const success = await handleDelete(deleteModalState.document);
    if (success) {
      setDeleteModalState({ isOpen: false, document: null });
    }
  };

  const renderContent = useMemo(() => {
    // show upload progress
    if (isUploading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon
            message={`Uploading ${selectedFile?.length} Attachments`}
          />

          <div className="loading-sub-text">This may take a moment</div>
        </div>
      );
    }

    // show initial loading state (first fetch)
    if (isLoading) {
      return (
        <div className="documents--Loading">
          <LoadingIcon message="Loading Documents" />
        </div>
      );
    }

    // show empty state
    if (!documents?.length) {
      return (
        <div
          className={cx("empty-state", {
            "empty-state--drag-active": isDragActive,
          })}
        >
          <div className="empty-state-content">
            <span className="material-icons">description</span>
            <h3>
              {documentSearchTerm
                ? `No Documents Found`
                : `No Documents Available`}
            </h3>
            {!documentSearchTerm && <p>Drag Documents Here to Upload</p>}
          </div>
        </div>
      );
    }

    // show table with data
    return (
      <RREDocumentTable documents={documents} onDelete={handleDocumentDelete} />
    );
  }, [
    documents,
    isLoading,
    isUploading,
    isDragActive,
    selectedFile,
    documentSearchTerm,
    handleDocumentDelete,
  ]);

  return (
    <>
      <div
        className={cx("documents documents-modal flex jc-se", {
          open: showDocumentsDrawer,
          "documents-modal--drag-active": isDragActive,
        })}
      >
        <input {...getInputProps()} />
        <div className="documents-wrapper w-search">
          <div className="header-primary flex jc-sb">
            Documents
            <a
              className="header-primary alt-btn"
              onClick={() => dispatch(setShowDocumentsDrawer(false))}
            >
              Close
            </a>
          </div>

          <div className="search-container">
            <SearchBar override={true} filterType="documents" />
          </div>

          <div {...getRootProps()} className="data-values mrg-top">
            {renderContent}
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModalState.isOpen}
        onClose={() => setDeleteModalState({ isOpen: false, document: null })}
        onConfirm={handleConfirmDelete}
        fileName={deleteModalState.document?.original_filename || ""}
      />
    </>
  );
};

export default RREDocuments;
